import React, { useEffect, useRef, useState } from 'react';
import { useHybridAuth } from '../../hooks/useHybridAuth';
import {
  validateInvitation,
  acceptInvitation,
} from '../../services/api/invitationApi';
import { useNavigate } from 'react-router-dom';
import { Alert } from '@relay-towing/ui';
import { devLog } from '../../utils/devLog';

interface DriverInfo {
  name: string;
  email: string;
  companyName: string;
  hasAccount: boolean;
}

export const InvitationScreen: React.FC = () => {
  const navigate = useNavigate();
  const {
    isAuthenticated,
    loginWithRedirect,
    user,
    isLoading,
    getAccessTokenSilently,
  } = useHybridAuth();

  const [token, setToken] = useState<string | null>(null);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isValidating, setIsValidating] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [driverInfo, setDriverInfo] = useState<DriverInfo | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  // Why the server refused, in its words (another email, already used), on
  // the screen rather than in a browser alert.
  const [refused, setRefused] = useState<string | null>(null);

  // Get token from localStorage (set by TokenHandler in App.tsx)
  useEffect(() => {
    const tokenFromStorage = localStorage.getItem('pending_invitation_token');
    devLog('[InvitationScreen] Checking for pending invitation token');
    devLog('[InvitationScreen] Auth state loaded', {
      isAuthenticated,
      isLoading,
      hasUser: !!user,
    });

    if (!tokenFromStorage) {
      devLog('[InvitationScreen] No invitation token in localStorage');
      // If no token, redirect to home
      navigate('/', { replace: true });
      return;
    }

    setToken(tokenFromStorage);
  }, [navigate]);

  // Validate token when it's available
  useEffect(() => {
    if (!token) return;

    const validateToken = async () => {
      setIsValidating(true);
      setValidationError(null);

      try {
        devLog('[InvitationScreen] Validating invitation token');
        const response = await validateInvitation(token);
        devLog('[InvitationScreen] Validation completed', {
          isValid: response.isValid,
        });

        setIsValid(response.isValid);

        if (response.isValid && response.driver) {
          setDriverInfo({
            name: response.driver.name,
            email: response.driver.email,
            companyName: response.driver.companyName,
            hasAccount: !!response.hasAccount,
          });
        } else {
          setValidationError('Invalid or expired invitation token');
        }
      } catch (error) {
        console.error('[InvitationScreen] Error validating token:', error);
        setIsValid(false);
        setValidationError('Failed to validate invitation token');
      } finally {
        setIsValidating(false);
        setIsPageLoading(false);
      }
    };

    validateToken();
  }, [token]);

  // Handle invitation acceptance after user is authenticated, once: after
  // a refusal the button tries again. A new user object re-ran it, and a
  // refusal was asked again until it gave way.
  const autoAccepted = useRef(false);
  useEffect(() => {
    const handlePostAuthAcceptance = async () => {
      // Debug logging to understand why auto-accept is triggering
      devLog('[InvitationScreen] Auto-accept check', {
        isValid,
        isAuthenticated,
        hasUserSub: !!user?.sub,
        hasToken: !!token,
        isAccepting,
      });

      if (
        isValid &&
        isAuthenticated &&
        user?.sub &&
        token &&
        !isAccepting &&
        !autoAccepted.current
      ) {
        autoAccepted.current = true;
        devLog(
          '[InvitationScreen] User authenticated, auto-accepting invitation'
        );
        await handleAcceptInvitation();
      }
    };

    handlePostAuthAcceptance();
  }, [isValid, isAuthenticated, user, token]);

  const handleAcceptInvitation = async () => {
    if (!token || !user?.sub) {
      console.error('[InvitationScreen] Missing token or user information');
      return;
    }

    devLog('[InvitationScreen] Accepting invitation');
    setIsAccepting(true);
    setRefused(null);
    try {
      await acceptInvitation(token, await getAccessTokenSilently());
      devLog('[InvitationScreen] Invitation accepted successfully');

      // Clear the token from localStorage since it's been used
      localStorage.removeItem('pending_invitation_token');

      // Straight into the app: an alert saying so only stood in the way.
      navigate('/dashboard', { replace: true });
    } catch (error) {
      // Refused for good (wrong email, used, expired): forgotten, so the
      // next person to sign in on this device is not sent back to it. Kept
      // on a network failure, to try again.
      const response = (
        error as { response?: { status?: number; data?: { message?: string } } }
      )?.response;
      const status = response?.status;
      if (status && status >= 400 && status < 500) {
        localStorage.removeItem('pending_invitation_token');
      }
      console.error('[InvitationScreen] Error accepting invitation:', error);
      setRefused(
        response?.data?.message ??
          'The invitation could not be accepted. Try again, or ask your manager for a new one.'
      );
    } finally {
      setIsAccepting(false);
    }
  };

  const handleAcceptButtonClick = () => {
    if (isAuthenticated) {
      // User is already authenticated, accept the invitation
      handleAcceptInvitation();
    } else {
      // One button for everyone: Auth0's sign-in for an address that has a
      // login, its sign-up otherwise, with the invited address filled in so
      // the account made is the one invited. A separate "I already have an
      // account" button was needed because sign-up answered "The user
      // already exists" to anyone removed from a company, who keeps theirs.
      loginWithRedirect({
        authorizationParams: {
          screen_hint: driverInfo?.hasAccount ? 'login' : 'signup',
          login_hint: driverInfo?.email,
          redirect_uri: `${window.location.origin}/invitation`,
        },
        appState: {
          returnTo: '/invitation',
        },
      });
    }
  };

  const handleLogin = () => {
    // Token is already stored in localStorage by TokenHandler
    devLog('[InvitationScreen] Redirecting to Auth0 signup');
    loginWithRedirect({
      authorizationParams: {
        screen_hint: 'signup',
      },
    });
  };

  if (isPageLoading || isValidating) {
    return (
      <div className="min-h-screen bg-primary flex flex-col items-center justify-center p-4">
        <div className="text-center">
          <svg
            className="animate-spin h-8 w-8 text-white mx-auto mb-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <p className="text-white">Validating invitation...</p>
        </div>
      </div>
    );
  }

  if (!token) {
    devLog('[InvitationScreen] No invitation token — displaying error message');
    return (
      <div className="min-h-screen bg-primary flex flex-col items-center justify-center p-4">
        <div className="bg-background p-6 rounded-lg max-w-md w-full">
          <h2 className="text-xl font-bold text-red-600 mb-4">
            Invalid Invitation Link
          </h2>
          <p className="text-gray-700 mb-6">
            The invitation link is missing a token. Please check your email and
            try again.
          </p>
          {isAuthenticated && (
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-primary text-primary-foreground py-3 rounded-lg font-medium"
            >
              Go to Dashboard
            </button>
          )}
          {!isAuthenticated && (
            <button
              onClick={handleLogin}
              className="w-full bg-primary text-primary-foreground py-3 rounded-lg font-medium"
            >
              Sign In / Sign Up
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!isValid) {
    return (
      <div className="min-h-screen bg-primary flex flex-col items-center justify-center p-4">
        <div className="bg-background p-6 rounded-lg max-w-md w-full">
          <h2 className="text-xl font-bold text-red-600 mb-4">
            Invalid or Expired Invitation
          </h2>
          <p className="text-gray-700 mb-6">
            This invitation may have expired or already been used. Please
            contact your manager for a new invitation.
          </p>
          {isAuthenticated && (
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-primary text-primary-foreground py-3 rounded-lg font-medium"
            >
              Go to Dashboard
            </button>
          )}
          {!isAuthenticated && (
            <button
              onClick={handleLogin}
              className="w-full bg-primary text-primary-foreground py-3 rounded-lg font-medium"
            >
              Sign In / Sign Up
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary flex flex-col items-center justify-center p-4">
      <div className="bg-background p-6 rounded-lg max-w-md w-full">
        <h2 className="text-2xl font-bold text-center mb-6 text-primary">
          Driver Invitation
        </h2>

        {driverInfo && (
          <div className="text-center mb-8">
            <p className="text-xl font-semibold mb-2 text-primary">
              Welcome, {driverInfo.name}!
            </p>
            <p className="text-gray-600">
              You've been invited to join {driverInfo.companyName} as a driver.
            </p>
          </div>
        )}

        {!isAuthenticated && (
          <p className="text-gray-700 mb-6 text-center">
            {driverInfo?.hasAccount
              ? 'Sign in to accept this invitation.'
              : 'Create your account to accept this invitation.'}
          </p>
        )}

        {refused && (
          <Alert
            tone="destructive"
            title="Couldn't accept the invitation"
            className="mb-6"
            data-testid="invitation-refused"
          >
            {refused}
          </Alert>
        )}

        <button
          onClick={handleAcceptButtonClick}
          disabled={isAccepting}
          className={`w-full ${isAccepting ? 'bg-gray-400' : 'bg-primary hover:bg-primary-dark'} text-primary-foreground py-3 rounded-lg font-medium transition-colors`}
        >
          {isAccepting
            ? 'Processing...'
            : refused
              ? 'Try again'
              : 'Accept Invitation'}
        </button>
      </div>
    </div>
  );
};
